import {Component, Input, OnInit, Type, OnDestroy} from '@angular/core';
import {IGetClass, IModalWindow, ISetOptions} from '../../../utils/interfaces/modal-dialog.interface';
import {ModalWindowsTypes} from '../../../core/enums';
import {IAudiomakerOptions} from '../../../shared/interfaces/AudiomakerOptions';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {saveAs} from 'file-saver';
import {CloudStorageDialogComponent, CloudStorageDialogOptions} from '../cloud-storage-dialog/cloud-storage-dialog.component';
import {ModalWindowService} from '../../../services/ui/modal-window.service';
import {MimeTypesEnum} from '../../../utils/enums';
import {TabService} from '../../../services/tab.service';
import {AudioMakerService} from '../../../services/audio-maker/audio-maker.service';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { SaveStatusEnum } from 'src/app/services/cloud-storage/enums/save-status.enum';



enum AudioFormat {
  mp3 = 'mp3',
  wav = 'wav'
}

@Component({
    selector: 'asg-audiomaker-dialog',
    templateUrl: './audiomaker-dialog.component.html',
    styleUrls: ['./audiomaker-dialog.component.scss'],
    standalone: true,
    imports: [FormsModule, ReactiveFormsModule, TranslateModule]
})

export class AudiomakerDialogComponent implements OnInit, OnDestroy, ISetOptions {
  @Input() ok: () => void;
  @Input() no: () => void;

  file: Blob;
  filename: string;
  audiomakerForm: FormGroup;
  locationArray: Array<string> = ['device', 'cloud'];
  format: AudioFormat;
  isLoaded: boolean = false;

  private readonly subscriptions: Subscription = new Subscription();
  private audioFormat: string = AudioFormat.mp3; // can later be a setting
  private selectedLocation: string = this.locationArray[0];

  constructor(
    private fb: FormBuilder,
    private modalWindowService: ModalWindowService,
    private tabService: TabService,
    private audiomakerService: AudioMakerService,
    private translate: TranslateService
  ) {
    this.filename = this.getFileName();
  }
  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngOnInit(): void {
    this.createForm();
    this.onValueChanges();
    this.isLoaded = true;
  }

  createForm(): void {
    this.audiomakerForm = this.fb.group({
      filenameControl: [this.filename],
      locationControl: [this.locationArray[0]],
    });
  }

  onValueChanges(): void {
    this.subscriptions.add(
      this.audiomakerForm.controls.locationControl.valueChanges.subscribe(
        e => this.selectedLocation = e
      )
    );

    this.subscriptions.add(
      this.audiomakerForm.controls.filenameControl.valueChanges.subscribe(
        e => this.filename = e
      )
    );
  }

  confirmLocation(): void {
    this.selectedLocation === this.locationArray[0] ?
      this.downloadAudioBlob(this.file, this.filename) :
      this.createFileAndSendToCloudDialog(this.file, this.filename);
  }

  deactivateAudiomaker(): void {
      this.audiomakerService.toggleAudioMakerFunction();
  }

  getWindowType(): Type<IModalWindow> {
    return AudiomakerDialogComponent;
  }

  getWindowEnumType(): ModalWindowsTypes {
    return ModalWindowsTypes.AUDIOMAKER_DIALOG;
  }

  getInstanceOf(type: string): IModalWindow | IGetClass | ISetOptions | null {
    switch (type) {
      case 'IModalWindow':
        return this as unknown as IModalWindow;
      case 'IGetClass':
        return this as unknown as IGetClass;
      case 'ISetOptions':
        return this as unknown as ISetOptions;
      default: return null;
    }
  }

  getClass(): string {
    return 'audiomaker-dlg';
  }

  setOptions(data: IAudiomakerOptions): void {
    this.file = data.file;
  }

  private getFileName(): string {
    const title = this.tabService.activeTab ? this.tabService.activeTab.title : '';
    const audioString = '-audio';
    if (title === 'COMMON.NEWTAB') {
      return this.translate.instant(title) + audioString;
    } else {
      return title.replace(/\.[^/.]+$/, '') + audioString;
    }
  }

  private downloadAudioBlob(blob: Blob, fileName: string): void {
    saveAs(blob, `${fileName}.${this.audioFormat}`);
    this.deactivateAudiomaker();
  }

  private createFileAndSendToCloudDialog(file: Blob, filename: string): void {
    const audioFile = this.convertBlobToFile(file, filename, MimeTypesEnum.Mp3);
    const options: CloudStorageDialogOptions = { file: audioFile, showAllFiles: true, dialogStatus: SaveStatusEnum.AudioSave};
    this.modalWindowService.createModalWindow(CloudStorageDialogComponent, options);
  }

  private convertBlobToFile(blob: Blob, filename: string, filetype: string): File {
    return new File(
      [blob], `${filename}.${AudioFormat.mp3}`,
      {type: filetype}
    );
  }
}
